/**
 * 生成中页（`FJGeneratingPage`）：对外 action 入口。
 * 统一通过 `FJGeneratingPageAction` 访问。
 */
import type { AppThunk } from '../appThunk';
import { FJ_COMMON_VIEW, setActiveView } from '../slice/FJCommonSlice';
import { setGeneratingVideoMeta } from '../slice/FJGeneratingPageSlice';
import { FJSettingPageAction } from './FJSettingPageAction';
import FJLongToShortStore from '@src/store/FJLongToShortStore';
import FJVideoUtil from '@src/util/FJVideoUtil';
import FJLocalStore from '@src/store/FJLocalStore';
import FJMessageStore, { MESSAGE_TYPE } from '@src/store/FJMessageStore';
import type { FJGeneratingVideoMeta } from '@src/types/FJProject';
import { FJModalsAction } from './FJModalsAction';
import FJStringUtil from '@src/util/FJStringUtil';
import FJUtil from '@src/util/FJUtil';
import FJLoadingModalSlice from '../slice/modal/FJLoadingModalSlice';
import FJRequirementsUtil from '@src/util/FJRequirementsUtil';
import { getLtsUploadSource, inferVideoFormat, LTS_PARSE_FAIL } from '@src/util/FJLongToShortEa';
import FJConfig from '@src/config/FJConfig';

/** 可上传时长 1–120 分钟（秒） */

const ALLOWED_VIDEO_EXTENSIONS = new Set(['mp4', 'mov', 'webm', 'm4v', 'avi', 'flv', 'mkv', 'wmv']);
const ALLOWED_VIDEO_MIME_TYPES = new Set([
    'video/mp4',
    'video/quicktime',
    'video/webm',
    'video/x-m4v',
    'video/x-msvideo',
    'video/vnd.avi',
    'video/avi',
    'video/x-flv',
    'video/x-matroska',
    'video/x-ms-wmv',
]);

function isAllowedGeneratingUploadVideo(file: File): boolean {
    const ext = file.name.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1];
    if (ext && ALLOWED_VIDEO_EXTENSIONS.has(ext)) {
        return true;
    }
    if (file.type && ALLOWED_VIDEO_MIME_TYPES.has(file.type)) {
        return true;
    }
    return false;
}

export type { FJGeneratingPageState, FJGeneratingTaskItem } from '../slice/FJGeneratingPageSlice';

export const FJGeneratingPageAction = {
    /** 生成中页返回主页（不清空已选视频与任务，仅切换视图） */
    backToHomeFromGeneratingAction: (): AppThunk => dispatch => {
        dispatch(setActiveView(FJ_COMMON_VIEW.START_LANDING));
    },

    removeVideoAction: (): AppThunk => dispatch => {
        FJLongToShortStore.clearFileData();
        dispatch(
            setGeneratingVideoMeta({
                thumbnailUrl: '',
                title: '',
                sourceUrl: '',
                resolutionLabel: '',
                youtubeKey: '',
                duration: 0,
            }),
        );
        dispatch(setActiveView(FJ_COMMON_VIEW.START_LANDING));
    },

    setVideoMetaAction:
        (
            file: File,

            options?: {
                sourceUrl?: string;
                resolutionLabel?: string;
                youtubeKey?: string;
                thumbnailUrl?: string;
                downloadUrl?: string;
                isExistS3?: boolean;
                fileName?: string;
            },

            checkIsCancel?: () => boolean,
            completeCallback?: () => void,
        ): AppThunk =>
        async (dispatch, getState) => {
            const {
                sourceUrl,
                resolutionLabel,
                youtubeKey,
                thumbnailUrl,
                fileName = '',
                downloadUrl,
                isExistS3,
            } = options ?? {};
            try {
                dispatch(FJLoadingModalSlice.actions.setIsCancel(false));
                const disableDownload = downloadUrl && isExistS3;
                const fileOrUrl = disableDownload ? downloadUrl : file;
                if (!disableDownload && file && file.size > 0) {
                    if (!isAllowedGeneratingUploadVideo(file)) {
                        FJRequirementsUtil.eaLog('long_to_shorts_vedio_parse_fail', {
                            source: getLtsUploadSource(),
                            fail_reason: LTS_PARSE_FAIL.FORMAT_ERROR,
                        });
                        FJMessageStore.addMessage(
                            FJLocalStore._('unsupported-format-please-upload-a-video-in-mp4-mov-etc'),
                            MESSAGE_TYPE.ERROR,
                        );
                        return;
                    }
                }

                let duration: number;
                let name: string;
                let width: number;
                let height: number;
                try {
                    ({ duration, name, width, height } = await FJVideoUtil.loadVideo(fileOrUrl));
                } catch {
                    FJRequirementsUtil.eaLog('long_to_shorts_vedio_parse_fail', {
                        source: getLtsUploadSource(),
                        fail_reason: LTS_PARSE_FAIL.LOAD_VIDEO_FAIL,
                    });
                    return;
                }
                if (checkIsCancel?.()) {
                    return;
                }
                if (disableDownload || (file && file.size > 0)) {
                    if (
                        !Number.isFinite(duration) ||
                        duration < FJConfig.VIDEO_UPLOAD_MIN_DURATION_SEC ||
                        duration > FJConfig.VIDEO_UPLOAD_MAX_DURATION_SEC
                    ) {
                        const fail_reason =
                            !Number.isFinite(duration) || duration < FJConfig.VIDEO_UPLOAD_MIN_DURATION_SEC
                                ? LTS_PARSE_FAIL.NOT_ENOUGH_TIME
                                : LTS_PARSE_FAIL.DURATION_EXCEEDS;
                        FJRequirementsUtil.eaLog('long_to_shorts_vedio_parse_fail', {
                            source: getLtsUploadSource(),
                            fail_reason,
                        });
                        FJMessageStore.addMessage(
                            FJLocalStore._('please-upload-a-video-between-1-and-120-minutes'),
                            MESSAGE_TYPE.ERROR,
                        );
                        return;
                    }
                    // 去除音频检测
                    // const hasAudible = await FJVideoUtil.hasAudibleAudioInVideoFile(file, duration, {
                    //     maxProbeSeconds: 20,
                    // });
                    // if (checkIsCancel?.()) {
                    //     return;
                    // }
                    // if (!hasAudible) {
                    //     FJRequirementsUtil.eaLog('long_to_shorts_vedio_parse_fail', {
                    //         source: getLtsUploadSource(),
                    //         fail_reason: LTS_PARSE_FAIL.NO_VOICE,
                    //     });
                    //     FJMessageStore.addMessage(
                    //         FJLocalStore._('no-speech-detected-in-this-video-please-try-a-video-with-voice'),
                    //         MESSAGE_TYPE.ERROR,
                    //     );
                    //     return;
                    // }
                }
                let blob: Blob | null = null;
                if (thumbnailUrl) {
                    blob = await FJUtil.getBlobByUrl(thumbnailUrl);
                }
                if (!blob) {
                    blob = await FJVideoUtil.getThumbnailAtTime(fileOrUrl, 0, { maxLongEdge: 500 });
                }

                if (getState().modal.loading.isCancel) {
                    return;
                }
                const nameNoPrefix = fileName || FJUtil.getBaseName(name);

                const title = FJStringUtil.sliceByteLen(nameNoPrefix, 50);
                const url = blob ? URL.createObjectURL(blob) : '';
                FJLongToShortStore.setFileData({
                    duration,
                    name: title,
                    thumbnailUrl: url,
                    file,
                    width,
                    height,
                    fileDownLoadUrl: downloadUrl,
                    isExistS3: isExistS3,
                    thumbnailBlob: blob,
                });

                const meta: Partial<FJGeneratingVideoMeta> = {
                    thumbnailUrl: url,
                    title: title,
                    sourceUrl: sourceUrl ?? '',
                    resolutionLabel: resolutionLabel ?? '',
                    duration,
                    youtubeKey: youtubeKey ?? '',
                };

                dispatch(setGeneratingVideoMeta(meta));
                FJRequirementsUtil.eaLog('long_to_shorts_video_parse_success', {
                    source: getLtsUploadSource(),
                    URL: (sourceUrl ?? '').trim() || '/',
                    duration: Number.isFinite(duration) ? duration : 0,
                    format: inferVideoFormat(fileOrUrl),
                });
                dispatch(FJSettingPageAction.openGenSettingsAction());
            } finally {
                completeCallback?.();
                dispatch(FJModalsAction.hideProgressModalAction());
                dispatch(FJModalsAction.hideLoadingModalAction());
            }
        },
} as const;
